import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, NgZone, OnDestroy, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { environment } from '@environments/environments';
import { BankAccountDrawerBaseComponent } from '@shared/components/page-layout/drawers/bank-account-drawer-base.component';
import { buildDropInThemeOverrides } from '@shared/components/page-layout/drawers/jpmorgan-drop-in-theme';
import { DropInUIEvent, DropInUIInstance, DropInUIModule } from '@shared/components/page-layout/drawers/jpmorgan-drop-in-ui.types';
import { BankAccountType } from '@shared/enums/bank-account.enum';

const DROP_IN_MOUNT_ELEMENT_ID = 'jpmorgan-bank-dropin-mount';

@Component({
    selector: 'app-jpmorgan-bank-account-drawer',
    templateUrl: './jpmorgan-bank-account-drawer.component.html',
    styleUrls: ['./jpmorgan-bank-account-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatCheckbox, MatFormField, MatLabel, MatHint, MatSelect, MatOption, MatInput, MatButton, TitleCasePipe]
})
export class JpMorganBankAccountDrawerComponent extends BankAccountDrawerBaseComponent implements OnDestroy {
    private ngZone = inject(NgZone);

    /** Used only by the edit-mode preview (bank name/account type/last4), same as Braintree's variant. */
    bankAccountType = BankAccountType;

    private dropIn: DropInUIInstance;
    private unsubscribeDropIn: () => void;

    protected setupGatewayFormControls(isEditMode: boolean): void {
        this.bankAccountForm.addControl('dropInReady', this.formBuilder.control(isEditMode, isEditMode ? [] : [Validators.requiredTrue]));
    }

    /**
     * Generated before the client-token request and sent as ?reference=; the backend correlates
     * its async payment notification back to this value, and we echo it as
     * externalBankAccountIdNonce when saving the account - same contract as the JPMorgan
     * credit-card drawer.
     */
    protected override generateTokenReference(): string {
        return crypto.randomUUID();
    }

    protected initializeGateway(token: string): void {
        if (this.userBankAccount?.id) {
            // Editing is metadata-only (nickname/sidemark) - no need to mount a live payment iframe.
            this.busy = false;
            this.changeDetectorRef.detectChanges();
            return;
        }

        this.ngZone.runOutsideAngular(async () => {
            try {
                const dropInModule = await import(/* @vite-ignore */ environment.jpMorganDropInUiUrl) as DropInUIModule;
                const DropInUI = dropInModule.default;

                this.dropIn = new DropInUI({
                    checkoutSessionToken: token,
                    controlledSubmit: true,
                    themeValueOverrides: buildDropInThemeOverrides(DROP_IN_MOUNT_ELEMENT_ID)
                });

                this.unsubscribeDropIn = this.dropIn.subscribe(event => this.onDropInEvent(event));
                this.dropIn.mount(DROP_IN_MOUNT_ELEMENT_ID);
            } catch {
                this.ngZone.run(() => {
                    this.busy = false;
                    this.alertService.errorToast('Error', 'Error initializing payment form. Please try again later.');
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    protected submitNewCard(): void {
        this.dropIn?.submit();
    }

    ngOnDestroy(): void {
        this.unsubscribeDropIn?.();
        this.dropIn?.clearAllSubscribers();
        try {
            this.dropIn?.unmount();
        } catch {
            // Nothing mounted (e.g. edit mode skipped gateway init) - safe to ignore.
        }
        super.ngOnDestroy();
    }

    private onDropInEvent(event: DropInUIEvent): void {
        switch (event.message) {
            // "FormLoadedAtLeastOnePaymentMethod" is the bundle's actual "Ready" enum value
            // (confirmed via decompiled drop-in-ui.mjs) - MountSuccess/InitialLoadComplete already
            // cover this same moment, so this case is belt-and-suspenders.
            case 'FormLoadedAtLeastOnePaymentMethod':
            case 'InitialLoadComplete':
            case 'MountSuccess':
                this.ngZone.run(() => {
                    this.busy = false;
                    this.bankAccountForm.controls['dropInReady'].markAsDirty();
                    this.bankAccountForm.controls['dropInReady'].setValue(true);
                    this.changeDetectorRef.detectChanges();
                });
                break;
            case 'PaymentSuccess':
                // externalBankAccountIdNonce is the reference we generated and sent as ?reference=
                // to the client-token call - NOT parsed from this event's payload. Unlike the
                // credit-card drawer, accountType/ownershipType aren't read from the payload here:
                // its eCheck shape hasn't been decompiled/confirmed, so they're left for the
                // backend to infer from the JPMorgan session rather than guessed at.
                this.ngZone.run(() => {
                    this.persistNewBankAccount({
                        externalBankAccountIdNonce: this.tokenReference
                    });
                });
                break;
            case 'PaymentError':
            case 'PaymentUnsuccessful':
            case 'PaymentValidationFailed':
                this.ngZone.run(() => {
                    this.busy = false;
                    this.alertService.errorToast('Error', 'Error validating bank account details. Please check your information and try again.');
                    this.changeDetectorRef.detectChanges();
                });
                break;
            default:
                break;
        }
    }
}
